/**
 * sessionSelector.ts — Navbar project/session dropdown (compact, next to user profile).
 *
 * UI only: switching updates activeSessionId + LangGraph thread id.
 * Workflow reload is handled by main.tsx via SESSION_CHANGED_EVENT.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { onAuthStateChange, getAuthState } from './authForm';
import { adoptBackendSession, dispatchSessionChanged, formatProjectDeleteMessage, formatProjectLabel, formatProjectSwitchMessage, getActiveSessionId, getDefaultNewProjectName, getFirstProjectSessionId, getProjectBySessionId, listProjects, loadRegistry, removeProjectFromRegistry, setActiveSessionId, syncRegistryFromBackend, } from '../sessionProjects';
import { createBackendSession, deleteBackendSession, listBackendSessions } from '../backendClient';
let boundUserId = null;
let menuOpen = false;
function getElements() {
    return {
        root: document.getElementById('session-selector'),
        trigger: document.getElementById('session-selector-trigger'),
        label: document.getElementById('session-selector-label'),
        menu: document.getElementById('session-selector-menu'),
        newBtn: document.getElementById('session-selector-new'),
    };
}
function closeMenu() {
    const { menu } = getElements();
    menu === null || menu === void 0 ? void 0 : menu.classList.remove('show');
    menuOpen = false;
}
function openMenu() {
    const { menu } = getElements();
    menu === null || menu === void 0 ? void 0 : menu.classList.add('show');
    menuOpen = true;
}
/** Refresh trigger label and dropdown items from localStorage registry. */
export function refreshSessionSelectorUI() {
    if (!boundUserId)
        return;
    const { label, menu } = getElements();
    if (!label || !menu)
        return;
    const activeId = getActiveSessionId(boundUserId);
    const activeProject = getProjectBySessionId(boundUserId, activeId);
    label.textContent = activeProject
        ? formatProjectLabel(activeProject)
        : `Project ${activeId}`;
    menu.innerHTML = '';
    const projects = listProjects(boundUserId);
    const canDelete = projects.length > 1;
    for (const project of projects) {
        const row = document.createElement('div');
        row.className = 'session-selector-row';
        if (project.sessionId === activeId) {
            row.classList.add('active');
        }
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'session-selector-item';
        item.dataset.sessionId = String(project.sessionId);
        item.textContent = formatProjectLabel(project);
        item.addEventListener('click', () => {
            void handleSelectSession(project.sessionId);
        });
        row.appendChild(item);
        if (canDelete) {
            const deleteBtn = document.createElement('button');
            deleteBtn.type = 'button';
            deleteBtn.className = 'session-selector-delete';
            deleteBtn.title = 'Supprimer ce projet';
            deleteBtn.setAttribute('aria-label', `Supprimer ${formatProjectLabel(project)}`);
            deleteBtn.textContent = '×';
            deleteBtn.addEventListener('click', (e) => {
                void handleDeleteProject(project.sessionId, e);
            });
            row.appendChild(deleteBtn);
        }
        menu.appendChild(row);
    }
}
function handleSelectSession(sessionId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!boundUserId)
            return;
        if (sessionId === getActiveSessionId(boundUserId)) {
            closeMenu();
            return;
        }
        const project = getProjectBySessionId(boundUserId, sessionId);
        if (!project)
            return;
        const confirmed = yield showSwitchProjectDialog(project);
        if (!confirmed)
            return;
        setActiveSessionId(boundUserId, sessionId);
        refreshSessionSelectorUI();
        closeMenu();
        dispatchSessionChanged(boundUserId, sessionId);
    });
}
/** Confirm before switching — shows project name and optional target. */
function showSwitchProjectDialog(project) {
    return new Promise((resolve) => {
        const modal = document.getElementById('switch-project-modal');
        const messageEl = document.getElementById('switch-project-modal-message');
        const confirmBtn = document.getElementById('switch-project-modal-confirm');
        const cancelBtn = document.getElementById('switch-project-modal-cancel');
        if (!modal || !messageEl || !confirmBtn || !cancelBtn) {
            console.error('[sessionSelector] Switch project modal elements not found');
            resolve(false);
            return;
        }
        messageEl.textContent = formatProjectSwitchMessage(project);
        modal.classList.remove('hidden');
        const cleanup = () => {
            modal.classList.add('hidden');
            confirmBtn.removeEventListener('click', onConfirm);
            cancelBtn.removeEventListener('click', onCancel);
            modal.removeEventListener('click', onBackdrop);
            document.removeEventListener('keydown', onEscape);
        };
        const finish = (value) => {
            cleanup();
            resolve(value);
        };
        const onConfirm = () => finish(true);
        const onCancel = () => finish(false);
        const onBackdrop = (e) => {
            if (e.target === modal)
                finish(false);
        };
        const onEscape = (e) => {
            if (e.key === 'Escape')
                finish(false);
        };
        confirmBtn.addEventListener('click', onConfirm);
        cancelBtn.addEventListener('click', onCancel);
        modal.addEventListener('click', onBackdrop);
        document.addEventListener('keydown', onEscape);
    });
}
/** Open naming dialog — resolves with trimmed name or null if cancelled. */
function showNewProjectDialog(defaultName) {
    return new Promise((resolve) => {
        const modal = document.getElementById('new-project-modal');
        const input = document.getElementById('new-project-name-input');
        const confirmBtn = document.getElementById('new-project-modal-confirm');
        const cancelBtn = document.getElementById('new-project-modal-cancel');
        if (!modal || !input || !confirmBtn || !cancelBtn) {
            console.error('[sessionSelector] New project modal elements not found');
            resolve(null);
            return;
        }
        input.value = defaultName;
        modal.classList.remove('hidden');
        // Focus + select default name so the user can type immediately.
        requestAnimationFrame(() => {
            input.focus();
            input.select();
        });
        const cleanup = () => {
            modal.classList.add('hidden');
            confirmBtn.removeEventListener('click', onConfirm);
            cancelBtn.removeEventListener('click', onCancel);
            modal.removeEventListener('click', onBackdrop);
            input.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keydown', onEscape);
        };
        const finish = (value) => {
            cleanup();
            resolve(value);
        };
        const onConfirm = () => {
            finish(input.value.trim() || defaultName);
        };
        const onCancel = () => {
            finish(null);
        };
        const onBackdrop = (e) => {
            if (e.target === modal)
                finish(null);
        };
        const onKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onConfirm();
            }
        };
        const onEscape = (e) => {
            if (e.key === 'Escape')
                finish(null);
        };
        confirmBtn.addEventListener('click', onConfirm);
        cancelBtn.addEventListener('click', onCancel);
        modal.addEventListener('click', onBackdrop);
        input.addEventListener('keydown', onKeyDown);
        document.addEventListener('keydown', onEscape);
    });
}
function handleNewProject() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!boundUserId)
            return;
        closeMenu();
        const defaultName = getDefaultNewProjectName(boundUserId);
        const projectName = yield showNewProjectDialog(defaultName);
        if (projectName === null)
            return;
        const created = yield createBackendSession(projectName);
        if (!created.success || created.sessionId == null) {
            console.error('[sessionSelector] Failed to create backend session:', created.error);
            return;
        }
        adoptBackendSession(boundUserId, created.sessionId, (_a = created.projectName) !== null && _a !== void 0 ? _a : projectName);
        refreshSessionSelectorUI();
        dispatchSessionChanged(boundUserId, created.sessionId);
    });
}
function handleDeleteProject(sessionId, e) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        e.stopPropagation();
        e.preventDefault();
        if (!boundUserId)
            return;
        const projects = listProjects(boundUserId);
        if (projects.length <= 1)
            return;
        closeMenu();
        const showConfirm = window;
        if (!showConfirm.showConfirmationModal) {
            console.error('[sessionSelector] showConfirmationModal not available');
            return;
        }
        const project = getProjectBySessionId(boundUserId, sessionId);
        if (!project)
            return;
        const isActiveProject = sessionId === getActiveSessionId(boundUserId);
        const confirmed = yield showConfirm.showConfirmationModal('Supprimer ce projet ?', formatProjectDeleteMessage(project, isActiveProject), 'Supprimer', 'Annuler');
        if (!confirmed)
            return;
        const result = yield deleteBackendSession(sessionId);
        if (!result.success) {
            console.error('[sessionSelector] Failed to delete session:', result.error);
            (_a = showConfirm.showToast) === null || _a === void 0 ? void 0 : _a.call(showConfirm, result.error || 'Échec de la suppression du projet', 'error');
            return;
        }
        const fallbackId = (_d = (_b = result.fallbackSessionId) !== null && _b !== void 0 ? _b : (_c = listProjects(boundUserId)
            .filter((p) => p.sessionId !== sessionId)
            .sort((a, b) => a.sessionId - b.sessionId)[0]) === null || _c === void 0 ? void 0 : _c.sessionId) !== null && _d !== void 0 ? _d : getFirstProjectSessionId(boundUserId);
        removeProjectFromRegistry(boundUserId, sessionId, fallbackId);
        setActiveSessionId(boundUserId, fallbackId);
        refreshSessionSelectorUI();
        dispatchSessionChanged(boundUserId, fallbackId);
        const fallbackProject = getProjectBySessionId(boundUserId, fallbackId);
        const fallbackLabel = fallbackProject
            ? formatProjectLabel(fallbackProject)
            : `Project ${fallbackId}`;
        (_e = showConfirm.showToast) === null || _e === void 0 ? void 0 : _e.call(showConfirm, isActiveProject
            ? `Projet supprimé — bascule vers « ${fallbackLabel} »`
            : 'Projet supprimé', 'success');
    });
}
function syncWithBackend(userId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const result = yield listBackendSessions();
        if (result.success && ((_a = result.sessions) === null || _a === void 0 ? void 0 : _a.length)) {
            syncRegistryFromBackend(userId, result.sessions);
        }
    });
}
function bindUser(userId) {
    boundUserId = userId;
    loadRegistry(userId);
    void syncWithBackend(userId).then(() => refreshSessionSelectorUI());
    const { root } = getElements();
    if (root)
        root.style.display = 'flex';
}
function unbindUser() {
    boundUserId = null;
    closeMenu();
    const { root } = getElements();
    if (root)
        root.style.display = 'none';
}
/** Call once from main.tsx on DOMContentLoaded. */
export function initSessionSelector() {
    const { trigger, newBtn, menu } = getElements();
    // Clicks inside the dropdown must not bubble to document (which closes the menu).
    menu === null || menu === void 0 ? void 0 : menu.addEventListener('click', (e) => {
        e.stopPropagation();
    });
    trigger === null || trigger === void 0 ? void 0 : trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menuOpen)
            closeMenu();
        else
            openMenu();
    });
    newBtn === null || newBtn === void 0 ? void 0 : newBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        void handleNewProject();
    });
    document.addEventListener('click', () => {
        if (menuOpen)
            closeMenu();
    });
    onAuthStateChange((user, authenticated) => {
        if (authenticated && (user === null || user === void 0 ? void 0 : user.username)) {
            bindUser(user.username);
        }
        else {
            unbindUser();
        }
    });
    // Auth is restored async on page load — bind immediately if already authenticated.
    const { user, isAuthenticated } = getAuthState();
    if (isAuthenticated && (user === null || user === void 0 ? void 0 : user.username)) {
        bindUser(user.username);
    }
}
